package app.footyguide.web.components.club

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.core.locale.AppStrings
import app.footyguide.core.theme.Club
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.web.components.ChannelBadgesRow
import app.footyguide.web.components.CrestSizes
import app.footyguide.web.components.Icons
import app.footyguide.web.components.LucideIcon
import app.footyguide.web.components.SafeImg
import app.footyguide.web.components.formatBannerDate
import app.footyguide.web.page.extractKickoffTime
import app.footyguide.web.route.WebRoutes

/**
 * Calendar upcoming matches list with opponent navigation.
 */
@Composable
fun ClubCalendarList(
    club: Club,
    matches: List<Game>,
    onNavigate: (String) -> Unit,
    strings: AppStrings
) {
    Div({ style { padding(0.px, 16.px, 20.px) } }) {
        if (matches.isEmpty()) {
            Div({
                style {
                    padding(48.px, 20.px)
                    backgroundColor(Color("#FFFFFF"))
                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                    borderRadius(16.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("text-align", "center")
                    gap(8.px)
                    property("box-shadow", "0 2px 6px rgba(22, 24, 29, 0.03)")
                }
            }) {
                LucideIcon(Icons.CALENDAR, size = 44, color = "var(--muted)")
                H3({
                    style {
                        margin(8.px, 0.px, 0.px)
                        fontSize(16.px)
                        fontWeight(800)
                        color(Color("#16181D"))
                    }
                }) {
                    Text(strings.noGamesScheduled)
                }
                P({
                    style {
                        margin(0.px)
                        color(Color("#71717A"))
                        fontSize(13.px)
                    }
                }) {
                    Text(strings.noGamesForTeam)
                }
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(10.px)
                }
            }) {
                matches.forEach { game ->
                    val isHome = game.homeTeamId == club.slug
                    val oppId = if (isHome) game.awayTeamId else game.homeTeamId
                    val oppClub = ClubCatalog.bySlug(oppId)

                    Div({
                        style {
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(14.px)
                            padding(14.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.SpaceBetween)
                                fontSize(12.px)
                                fontFamily("Inter, sans-serif")
                                fontWeight(600)
                                color(Color("#71717A"))
                            }
                        }) {
                            Span {
                                Text("${formatBannerDate(game.date, strings)} · ${extractKickoffTime(game.kickoff)}")
                            }
                            Span({
                                style {
                                    fontSize(10.px)
                                    fontWeight(700)
                                    letterSpacing(0.5.px)
                                    padding(2.px, 6.px)
                                    borderRadius(4.px)
                                    backgroundColor(Color("#F4F4F5"))
                                    color(Color(if (isHome) "#16181D" else "#71717A"))
                                    property("text-transform", "uppercase")
                                }
                            }) {
                                Text(if (isHome) strings.homeLabel else strings.awayLabel)
                            }
                        }

                        // Match & Opponent Click Block
                        Div({
                            onClick { onNavigate(WebRoutes.game(game)) }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.SpaceBetween)
                                cursor("pointer")
                                padding(4.px, 0.px)
                            }
                        }) {
                            Div({
                                if (oppClub != null) {
                                    onClick { event ->
                                        event.stopPropagation()
                                        onNavigate(WebRoutes.club(oppClub.slug))
                                    }
                                }
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(10.px)
                                    cursor("pointer")
                                }
                            }) {
                                SafeImg(
                                    src = oppClub?.logoUrl,
                                    fallbackIcon = Icons.SHIELD,
                                    fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                                    modifier = {
                                        width(CrestSizes.MATCH_CARD_PX.px)
                                        height(CrestSizes.MATCH_CARD_PX.px)
                                        property("width", CrestSizes.MATCH_CARD_CSS)
                                        property("height", CrestSizes.MATCH_CARD_CSS)
                                        property("object-fit", "contain")
                                    }
                                )
                                val oppDisplayName = formatTeamDisplayName(oppId)
                                Span({
                                    style {
                                        fontFamily("Sora, sans-serif")
                                        fontSize(15.px)
                                        fontWeight(800)
                                        color(Color("#16181D"))
                                    }
                                }) {
                                    Text(if (isHome) "${club.name} × $oppDisplayName" else "$oppDisplayName × ${club.name}")
                                }
                            }
                            LucideIcon(Icons.CHEVRON_RIGHT, size = 18, color = "#71717A")
                        }

                        Div({
                            onClick { onNavigate(WebRoutes.game(game)) }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                paddingTop(6.px)
                                property("border-top", "1px solid #F4F4F5")
                                cursor("pointer")
                            }
                        }) {
                            ChannelBadgesRow(game.channelsFor("BR"))
                        }
                    }
                }
            }
        }
    }
}
